/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { FlipButton } from '../ActiveCardBack/partials/FlipButton';
import { copyToClipboard } from '../../../../utilities/helpers';

const BODY_FILL = '#1F1D20';
const BAND_HEIGHT = 48;
const LABEL_FILL = '#C9C9CC';
const VALUE_FILL = '#FFFFFF';
const LEFT_X = 16;
const CVV_X = 141;
const ROWS = { name: 76, number: 122, footer: 170 };
const VALUE_OFFSET = 18;
const COPY_ICON_FILL = '#8E8E93';
const COPY_ICON_X = { number: 176, expiry: 64, cvv: 174 };
const COPY_ICON_SIZE = 10;
const COPY_HIT_SIZE = 40;

const copyIconY = (row) => row + VALUE_OFFSET - COPY_ICON_SIZE - 1;

const groupDigits = (value) => value?.match(/.{1,4}/g)?.join(' ') ?? '';

const formatExpiry = (validThrough) => {
  if (!validThrough) return '';
  const [year, month] = validThrough.split('/');
  return `${month}/${year.slice(2, 4)}`;
};

const Field = ({ x, y, label, value, onClick = null }) => (
  <g>
    <text fill={LABEL_FILL} fontSize={11} x={x} y={y}>
      {label}
    </text>
    <text
      fill={VALUE_FILL}
      fontSize={14}
      x={x}
      y={y + VALUE_OFFSET}
      className={onClick ? 'cursor-pointer' : undefined}
      onClick={onClick || undefined}>
      {value}
    </text>
  </g>
);

Field.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const CopyButton = ({ x, y, label, onCopy }) => {
  const handleClick = (event) => {
    event.stopPropagation();
    onCopy();
  };

  return (
    <g role="button" aria-label={label} className="cursor-pointer" onClick={handleClick}>
      <rect
        x={x - (COPY_HIT_SIZE - COPY_ICON_SIZE) / 2}
        y={y - (COPY_HIT_SIZE - COPY_ICON_SIZE) / 2}
        width={COPY_HIT_SIZE}
        height={COPY_HIT_SIZE}
        fill="transparent"
      />
      <rect
        x={x + 3}
        y={y}
        width={COPY_ICON_SIZE}
        height={COPY_ICON_SIZE}
        rx={2}
        stroke={COPY_ICON_FILL}
        strokeWidth={1.5}
      />
      <rect
        x={x}
        y={y + 3}
        width={COPY_ICON_SIZE}
        height={COPY_ICON_SIZE}
        rx={2}
        fill={BODY_FILL}
        stroke={COPY_ICON_FILL}
        strokeWidth={1.5}
      />
    </g>
  );
};

CopyButton.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
};

const MonavateCardBack = ({
  details = null,
  isShowSecretInfo = false,
  securityNumbersForm,
  flip,
  toggleCvvModal,
  showSecretInfo,
}) => {
  const { pan, cvc, isLoadingPAN, isLoadingCVC, panError, cvcError } = securityNumbersForm || {};
  const isPanRevealed = isShowSecretInfo && !isLoadingPAN && !panError && Boolean(pan);
  const isCvcRevealed = isShowSecretInfo && !isLoadingCVC && !cvcError && Boolean(cvc);

  const expiry = formatExpiry(details?.cardValidThrough);

  const handleFlip = () => {
    flip();
    showSecretInfo();
  };

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 370 218"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <rect width="370" height="218" rx="12" fill={BODY_FILL} />
      <path
        d={`M0 12a12 12 0 0 1 12-12h346a12 12 0 0 1 12 12v${BAND_HEIGHT - 12}H0z`}
        fill="#000000"
      />
      <Field
        x={LEFT_X}
        y={ROWS.name}
        label="Cardholder name"
        value={details?.cardDisplayName || ''}
      />
      <Field
        x={LEFT_X}
        y={ROWS.number}
        label="Card number"
        value={groupDigits(isPanRevealed ? pan : details?.cardNumberObscured)}
        onClick={isPanRevealed ? null : toggleCvvModal}
      />
      <CopyButton
        x={COPY_ICON_X.number}
        y={copyIconY(ROWS.number)}
        label="Copy card number"
        onCopy={isPanRevealed ? () => copyToClipboard(pan, true) : toggleCvvModal}
      />
      <Field x={LEFT_X} y={ROWS.footer} label="Expiry date" value={expiry} />
      {expiry && (
        <CopyButton
          x={COPY_ICON_X.expiry}
          y={copyIconY(ROWS.footer)}
          label="Copy expiry date"
          onCopy={() => copyToClipboard(expiry)}
        />
      )}
      <Field
        x={CVV_X}
        y={ROWS.footer}
        label="CVV"
        value={isCvcRevealed ? cvc : '***'}
        onClick={isCvcRevealed ? null : toggleCvvModal}
      />
      <CopyButton
        x={COPY_ICON_X.cvv}
        y={copyIconY(ROWS.footer)}
        label="Copy CVV"
        onCopy={isCvcRevealed ? () => copyToClipboard(cvc) : toggleCvvModal}
      />
      <FlipButton onClick={handleFlip} />
    </svg>
  );
};

MonavateCardBack.propTypes = {
  details: PropTypes.object,
  isShowSecretInfo: PropTypes.bool,
  securityNumbersForm: PropTypes.object.isRequired,
  flip: PropTypes.func.isRequired,
  toggleCvvModal: PropTypes.func.isRequired,
  showSecretInfo: PropTypes.func.isRequired,
};

export default MonavateCardBack;
